"use client"

import { subscribeRestockNotification } from "@lib/data/products"
import { Button, Input, Text } from "@modules/common/components/ui"
import { FormEvent, useState } from "react"

type NotifyRestockFormProps = {
  sku: string
}

// Quiet secondary path inside the backorder panel: shoppers who would rather
// wait for stock than backorder can leave an email for a one-time
// back-in-stock notification.
export default function NotifyRestockForm({ sku }: NotifyRestockFormProps) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle")

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!email || status === "submitting") {
      return
    }

    setStatus("submitting")

    const subscribed = await subscribeRestockNotification(email, sku)

    setStatus(subscribed ? "success" : "error")
  }

  if (status === "success") {
    return (
      <Text
        role="status"
        className="text-sm font-medium"
        data-testid="notify-restock-success"
      >
        We&rsquo;ll email you the moment it&rsquo;s back.
      </Text>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start text-sm underline underline-offset-2 hover:text-amber-900"
        data-testid="notify-restock-toggle"
      >
        Prefer to wait? Email me when it&rsquo;s back in stock
      </button>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-y-2"
      data-testid="notify-restock-form"
    >
      <div className="flex gap-x-2">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address for restock notification"
          className="h-9 flex-1 bg-white"
          data-testid="notify-restock-email"
        />
        <Button
          type="submit"
          variant="secondary"
          size="small"
          className="h-9 shrink-0"
          isLoading={status === "submitting"}
          data-testid="notify-restock-submit"
        >
          Notify me
        </Button>
      </div>
      {status === "error" && (
        <Text
          role="alert"
          className="text-sm text-red-600"
          data-testid="notify-restock-error"
        >
          Couldn&rsquo;t sign you up &mdash; please try again
        </Text>
      )}
    </form>
  )
}
